import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { fetchGraphCached } from './lib/api';
import { colorForType, deriveUrl, getIdType, getNodeColors, getNodeDisplayType, GraphData, NODE_STYLES } from './lib/normalize';

const Graph = React.lazy(() => import('./components/Graph'));

const embedFalseValues = new Set(['0', 'false', 'no', 'off']);
const legendTypes = ['post', 'article', 'page', 'tag'] as const;

function detectEmbedMode() {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('embed');
  const hasEmbedParam = params.has('embed');
  const paramEnabled = hasEmbedParam && (!raw || !embedFalseValues.has(raw.toLowerCase()));
  let inFrame = false;
  try {
    inFrame = window.self !== window.top;
  } catch (err) {
    inFrame = true;
  }
  return paramEnabled || inFrame;
}

function getEmbedFocusId() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('focus');
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export default function App() {
  const [data, setData] = useState<GraphData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [query, setQuery] = useState('');
  const [hideHashNodes, setHideHashNodes] = useState<boolean>(true);
  const [hidePlainTagNodes, setHidePlainTagNodes] = useState<boolean>(true);
  const [hideExternalNodes, setHideExternalNodes] = useState<boolean>(false);
  const [lastUpdated, setLastUpdated] = useState<string | undefined>(undefined);
  const isEmbed = useMemo(detectEmbedMode, []);
  const embedFocusId = useMemo(getEmbedFocusId, []);
  const isEmbedFocus = isEmbed && Boolean(embedFocusId);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const adapted = await fetchGraphCached();
      setData(adapted);
      setLastUpdated(adapted.updatedAt);
    } catch (e: any) {
      console.warn('Failed to fetch graph', e);
      setError(e?.message ?? 'Failed to fetch graph');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isEmbed) return undefined;
    const handler = () => {
      if (document.visibilityState === 'visible') {
        // Revalidate on tab focus
        load();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [isEmbed, load]);

  const nodeColors = useMemo(() => (data ? getNodeColors(data) : new Map<string, string>()), [data]);
  const getNodeColor = useCallback(
    (id: string) => nodeColors.get(id) ?? colorForType(getIdType(id)),
    [nodeColors]
  );

  const { graphData, embedNeighbors, embedFocusNode, embedLinkDirections } = useMemo(() => {
    if (!data) {
      return {
        graphData: null,
        embedNeighbors: [] as GraphData['nodes'],
        embedFocusNode: null as GraphData['nodes'][number] | null,
        embedLinkDirections: new Map<string, { incoming: boolean; outgoing: boolean }>()
      };
    }
    if (isEmbed && embedFocusId) {
      const nodeById = new Map(data.nodes.map((n) => [n.id, n]));
      const neighborIds = new Set<string>();
      const outgoing = new Set<string>();
      const incoming = new Set<string>();
      data.links.forEach((l) => {
        const sourceId = String(l.source);
        const targetId = String(l.target);
        if (sourceId === embedFocusId) {
          neighborIds.add(targetId);
          outgoing.add(targetId);
        }
        if (targetId === embedFocusId) {
          neighborIds.add(sourceId);
          incoming.add(sourceId);
        }
      });
      const localIds = new Set<string>([embedFocusId, ...neighborIds]);
      const nodes = data.nodes.filter((n) => localIds.has(n.id));
      const links = data.links.filter(
        (l) => localIds.has(String(l.source)) && localIds.has(String(l.target))
      );
      const neighbors = Array.from(neighborIds)
        .map((id) => nodeById.get(id))
        .filter((n): n is GraphData['nodes'][number] => Boolean(n));
      const directionMap = new Map<string, { incoming: boolean; outgoing: boolean }>();
      neighborIds.forEach((id) => {
        directionMap.set(id, { incoming: incoming.has(id), outgoing: outgoing.has(id) });
      });
      return {
        graphData: { ...data, nodes, links } as GraphData,
        embedNeighbors: neighbors,
        embedFocusNode: nodeById.get(embedFocusId) ?? null,
        embedLinkDirections: directionMap
      };
    }
    const q = query.trim().toLowerCase();
    if (!q) {
      return {
        graphData: data,
        embedNeighbors: [],
        embedFocusNode: null,
        embedLinkDirections: new Map()
      };
    }
    const nodeSet = new Set(
      data.nodes
        .filter((n) => n.name.toLowerCase().includes(q) || n.id.toLowerCase().includes(q))
        .map((n) => n.id)
    );
    const neighborSet = new Set<string>(nodeSet);
    data.links.forEach((l) => {
      if (nodeSet.has(String(l.source)) || nodeSet.has(String(l.target))) {
        neighborSet.add(String(l.source));
        neighborSet.add(String(l.target));
      }
    });
    const nodes = data.nodes.filter((n) => neighborSet.has(n.id));
    const links = data.links.filter(
      (l) => neighborSet.has(String(l.source)) && neighborSet.has(String(l.target))
    );
    return {
      graphData: { ...data, nodes, links } as GraphData,
      embedNeighbors: [],
      embedFocusNode: null,
      embedLinkDirections: new Map()
    };
  }, [data, isEmbed, embedFocusId, query]);

  const filteredEmbedNeighbors = useMemo(() => {
    if (!isEmbed || !embedFocusId) return [];
    let filtered = embedNeighbors;
    if (hideHashNodes) {
      filtered = filtered.filter((node) => !node.name.startsWith('#'));
    }
    if (hidePlainTagNodes) {
      filtered = filtered.filter(
        (node) => getIdType(node.id) !== 'tag' || node.name.includes('#')
      );
    }
    if (hideExternalNodes) {
      filtered = filtered.filter((node) => getIdType(node.id) !== 'external');
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [embedNeighbors, embedFocusId, hideExternalNodes, hideHashNodes, hidePlainTagNodes, isEmbed]);

  const handleNodeClick = useCallback(
    (node: GraphData['nodes'][number]) => {
      const id = node.id;
      const name = (node as any).name;
      const href = (node as any).href as string | undefined;
      const url = href && href.length > 0 ? href : deriveUrl(id);
      if (!url) {
        console.warn('No URL available for node', id, name);
        return;
      }
      if (isEmbed) {
        window.open(url, '_top');
      } else {
        window.open(url, '_blank', 'noopener');
      }
    },
    [isEmbed]
  );

  const renderGraph = () => {
    if (loading) return <div className="status">Loading graph…</div>;
    if (error) {
      return (
        <div className="status status--error">
          <div>Error: {error}</div>
          <button onClick={load}>Retry</button>
        </div>
      );
    }
    if (!graphData) return null;
    return (
      <Suspense fallback={<div className="status">Loading viewer…</div>}>
        <Graph
          data={graphData}
          getNodeColor={getNodeColor}
          onNodeClick={handleNodeClick}
          hideHashNodes={hideHashNodes}
          hidePlainTagNodes={hidePlainTagNodes}
          hideExternalNodes={hideExternalNodes}
          focusNodeId={embedFocusId ?? undefined}
        />
      </Suspense>
    );
  };

  const renderNeighbors = () => {
    if (!isEmbedFocus) return null;
    return (
      <section className="app__neighbors">
        {filteredEmbedNeighbors.length === 0 ? (
          <div className="app__neighbors-empty">No connected nodes.</div>
        ) : (
          <ul className="app__neighbors-list">
            {filteredEmbedNeighbors.map((node) => {
              const href = node.href && node.href.length > 0 ? node.href : deriveUrl(node.id);
              if (!href) return null;
              const direction = embedLinkDirections.get(node.id);
              const directionKey =
                direction?.incoming && direction?.outgoing
                  ? 'bidirectional'
                  : direction?.outgoing
                    ? 'outgoing'
                    : 'incoming';
              const type = getNodeDisplayType(node);
              return (
                <li
                  key={node.id}
                  className={`app__neighbors-item app__neighbors-item--${type}`}
                  style={{ '--type-color': colorForType(type) } as React.CSSProperties}
                >
                  <span
                      className={`app__neighbors-marker app__neighbors-marker--${directionKey}`}
                      aria-label={directionKey}
                      title={directionKey}
                    >
                  </span>
                  <a href={href} target="_top" rel="noopener">
                    <span>
                      {node.name}
                    </span>
                  </a>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    );
  };

  return (
    <div className={`app${isEmbed ? ' app--embed' : ''}${isEmbedFocus ? ' app--embed-focus' : ''}`}>
      {isEmbed && !isEmbedFocus ? (
        <header className="app__header app__header--embed">
          <div className="app__controls">
            <label className="app__checkbox">
              <input
                type="checkbox"
                checked={hideHashNodes}
                onChange={(e) => setHideHashNodes(e.target.checked)}
              />
              Hide nodes with # tags
            </label>
            <label className="app__checkbox">
              <input
                type="checkbox"
                checked={hidePlainTagNodes}
                onChange={(e) => setHidePlainTagNodes(e.target.checked)}
              />
              Hide nodes without # tags
            </label>
            <label className="app__checkbox">
              <input
                type="checkbox"
                checked={hideExternalNodes}
                onChange={(e) => setHideExternalNodes(e.target.checked)}
              />
              Hide external nodes
            </label>
          </div>
        </header>
      ) : !isEmbed ? (
        <header className="app__header">
          <h1>Guji Graph Viewer</h1>
          <div className="app__controls">
            <input
              type="search"
              placeholder="Search nodes..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search nodes"
            />
            <label className="app__checkbox">
              <input
                type="checkbox"
                checked={hideHashNodes}
                onChange={(e) => setHideHashNodes(e.target.checked)}
              />
              Hide nodes with # tags
            </label>
            <label className="app__checkbox">
              <input
                type="checkbox"
                checked={hidePlainTagNodes}
                onChange={(e) => setHidePlainTagNodes(e.target.checked)}
              />
              Hide nodes without # tags
            </label>
            <label className="app__checkbox">
              <input
                type="checkbox"
                checked={hideExternalNodes}
                onChange={(e) => setHideExternalNodes(e.target.checked)}
              />
              Hide external nodes
            </label>
            <button onClick={load} title="Refresh graph">⟳ Refresh</button>
          </div>
        </header>
      ) : null}
      {lastUpdated && !isEmbed && (
        <div className="app__meta">Updated {new Date(lastUpdated).toLocaleString()}</div>
      )}
      {isEmbedFocus ? (
        <div className="app__embed-grid">
          <div className="app__embed-graph">{renderGraph()}</div>
          {renderNeighbors()}
        </div>
      ) : (
        renderGraph()
      )}
      {!isEmbed && (
        <footer className="app__footer">
          <span>Color by type: </span>
          {legendTypes.map((type) => (
            <span key={type} className="legend" style={{ '--c': NODE_STYLES[type].color } as React.CSSProperties}>
              {NODE_STYLES[type].label}
            </span>
          ))}
        </footer>
      )}
    </div>
  );
}
